import { getRotationCenter } from 'src/utils/toolbelt';

import { borderFlagsBottom, borderFlagsLeft, borderFlagsRight, borderFlagsTop, cellSizeMin, directionHorizontal, directionVertical } from './table';

export default {
  // Describe a table
  getTableData (tableElement) {
    const rect = tableElement.querySelector('rect');

    return {
      x: parseFloat(rect.getAttribute('x')),
      y: parseFloat(rect.getAttribute('y')),
      width: parseFloat(rect.getAttribute('width')),
      height: parseFloat(rect.getAttribute('height'))
    };
  },

  // Get the group which contains table borders
  getTableBordersElement (tableElement) {
    return tableElement.querySelector('g.table-borders');
  },

  // Get all row elements in the table
  getAllRowElements (tableElement) {
    return tableElement.querySelectorAll('g.table-row');
  },

  getTableState (tableElement) {
    const rows = Array.from(tableElement.querySelectorAll('.table-row'));

    return rows.map(row => {
      const cells = Array.from(row.querySelectorAll('.table-cell'));

      return cells.map(cell => {
        if (this.isMergeSlave(cell)) return null;

        return cell.querySelector('rect').classList.contains('table-cell-selected');
      });
    });
  },

  // Get a row element with the given index
  getRowElementAt (tableElement, rowIndex) {
    return tableElement.querySelector(`g.table-row.index-${rowIndex}`);
  },

  // Get row description
  // Arguments are Booleans indicating if those info should be included
  // For a row, position and size data are extracted together
  getRowData (rowElement, index, positionAndSize) {
    let rowIndex = null;

    if (index) {
      const indexClassName = [...rowElement.classList].find(className => className.startsWith('index'));
      rowIndex = parseInt(indexClassName.substr(6));
    }

    const CellElements = this.getAllCellElementsByRow(rowElement);

    if (!CellElements.length || !positionAndSize) {
      return { rowIndex };
    }

    let x = null;
    let y = null;
    let width = 0;
    let height = 0;

    CellElements.forEach(CellElement => {
      const cellData = this.getCellData(CellElement, false, true, true, false);

      if (x === null || cellData.x < x) {
        x = cellData.x;
      }

      if (y === null || cellData.y < y) {
        y = cellData.y;
      }

      // If cell is minimized (for a merge operation), skip that
      if (!this.isMergeSlave(CellElement)) width += cellData.width;

      // If a cell is merged and it is the master, then ignore its height (as it can extend to multiple rows)
      if (cellData.height > height && !this.isMergeMaster(CellElement)) {
        height = cellData.height;
      }
    });

    return { rowIndex, x, y, width, height };
  },

  getTransformedCellData (cellElement, bbox, position, size, transform) {
    if (!transform) return bbox;
    if (!position && !size) return bbox;

    const parentText = cellElement.querySelector('g.textarea');
    if (!parentText) return bbox;

    const { x: cx, y: cy } = getRotationCenter(parentText);
    const angle = svgedit.utilities.getRotationAngle(parentText);
    if (!angle) return bbox;

    const newPoints = INKIVE.Helpers.getTransformPoints(bbox, 0, angle, cx, cy);
    const isVerticalRotation = angle === 90 || angle === 270;
    return {
      x: Math.min(newPoints.tr.x, newPoints.tl.x, newPoints.br.x, newPoints.bl.x),
      y: Math.min(newPoints.tr.y, newPoints.tl.y, newPoints.br.y, newPoints.bl.y),
      width: isVerticalRotation ? bbox.height : bbox.width,
      height: isVerticalRotation ? bbox.width : bbox.height,
      angle
    };
  },

  // Get cell description
  // Arguments are Booleans indicating if those info should be included
  // TODO: refactor https://github.com/Desygner/desygner-editor/issues/1387
  getCellData (cellElement, indices, position, size, visual, transform = true) {
    const rect = cellElement.querySelector('rect');
    const bbox = {
      x: position ? parseFloat(rect.getAttribute('x')) : 0,
      y: position ? parseFloat(rect.getAttribute('y')) : 0,
      width: size ? parseFloat(rect.getAttribute('width')) : 0,
      height: size ? parseFloat(rect.getAttribute('height')) : 0
    };

    const transformedBbox = this.getTransformedCellData(cellElement, bbox, position, size, transform);

    let rowIndex = null;
    let columnIndex = null;
    let x = null;
    let y = null;
    let width = null;
    let height = null;
    let fill = null;
    let angle = null;

    const classList = [...cellElement.classList];

    if (indices) {
      let indexClassName = classList.find(className => className.startsWith('index-r'));
      rowIndex = parseInt(indexClassName.substr(8));

      indexClassName = classList.find(className => className.startsWith('index-c'));
      columnIndex = parseInt(indexClassName.substr(8));
    }

    if (position) {
      x = transformedBbox.x;
      y = transformedBbox.y;
    }

    if (size) {
      width = transformedBbox.width;
      height = transformedBbox.height;
    }

    if (visual) {
      fill = rect.getAttribute('fill');
    }

    if (transform) {
      angle = Number(transformedBbox.angle) || null;
    }

    return { rowIndex, columnIndex, x, y, width, height, fill, angle, classList };
  },

  getCellIndexData (cellElement) {
    const classList = [...cellElement.classList];
    const rowClass = classList.find(className => className.startsWith('index-r'));
    const rowIndex = parseInt(rowClass.substr(8));

    const columnClass = classList.find(className => className.startsWith('index-c'));
    const columnIndex = parseInt(columnClass.substr(8));

    return { rowIndex, columnIndex };
  },

  getRowIndexData (rowElement) {
    const indexClassName = [...rowElement.classList].find(className => className.startsWith('index'));
    return parseInt(indexClassName.substr(6));
  },

  getMasterCellDimension (tableElement, cellElement) {
    const { rowIndex, columnIndex } = this.getCellIndexData(cellElement);
    const slaves = [
      this.getCellElementAt(tableElement, rowIndex + 1, columnIndex),
      this.getCellElementAt(tableElement, rowIndex, columnIndex + 1)
    ].filter(element => element && this.isMergeSlave(element));

    if (!slaves.length) return { width: null, height: null };

    const bestSlave = slaves[0];

    const slaveX = parseFloat(bestSlave.querySelector('rect').getAttribute('x'));
    const slaveY = parseFloat(bestSlave.querySelector('rect').getAttribute('y'));
    const slaveWidth = parseFloat(bestSlave.querySelector('rect').getAttribute('width'));
    const slaveHeight = parseFloat(bestSlave.querySelector('rect').getAttribute('height'));

    const cellX = parseFloat(cellElement.querySelector('rect').getAttribute('x'));
    const cellY = parseFloat(cellElement.querySelector('rect').getAttribute('y'));

    const width = slaveX > cellX ? slaveX - cellX : slaveWidth;
    const height = slaveY > cellY ? slaveY - cellY : slaveHeight;

    return { width, height };
  },

  // Get all cells of the table
  getAllCellElements (tableElement) {
    return tableElement.querySelectorAll('g.table-cell');
  },

  // Get a cell element at the given coordinates
  getCellElementAt (tableElement, cellRowIndex, cellColumnIndex) {
    return tableElement.querySelector(`g.table-cell.index-r-${cellRowIndex}.index-c-${cellColumnIndex}`);
  },

  // Get all cell elements that belong to the row
  getAllCellElementsByRow (rowElement) {
    return rowElement.querySelectorAll('g.table-cell');
  },

  // Get all cell elements in the given row index
  getAllCellElementsAtRow (tableElement, rowIndex) {
    return tableElement.querySelectorAll(`g.table-cell.index-r-${rowIndex}`);
  },

  // Get all cell elements in the given column index
  getAllCellElementsAtColumn (tableElement, columnIndex) {
    return tableElement.querySelectorAll(`g.table-cell.index-c-${columnIndex}`);
  },

  // Get cell text description
  // Arguments are booleans indicating if those info should be included
  getCellTextData (cellElement, font, text, alignment) {
    let fontSize = null;
    let fontWeight = null;
    let fontStyle = null;
    let fontFamily = null;
    let fill = null;
    let textDecoration = null;
    let highlight = null;
    let texts = null;
    let textAnchor = null;
    let verticalAlignment = null;
    let isOrderedList = null;
    let isBulletsList = null;
    let isUpperCase = null;

    let textElement = null;
    if (font) {
      textElement = cellElement.querySelector('g.text');
      fontSize = parseFloat(textElement.getAttribute('font-size'));
      fontWeight = textElement.getAttribute('font-weight');
      if (fontWeight) {
        fontWeight = fontWeight === 'bold' ? 700 : parseFloat(fontWeight);
      }
      fontStyle = textElement.getAttribute('font-style');
      fontFamily = textElement.getAttribute('font-family');
      fill = textElement.getAttribute('fill');
      textDecoration = textElement.getAttribute('text-decoration');
      highlight = textElement.getAttribute('highlight');
      isOrderedList = !!textElement.querySelector('.ordered');
      isBulletsList = !!textElement.querySelector('.bullets');
      isUpperCase = textElement.textContent !== textElement.textContent.toLowerCase();
    }

    if (text) {
    // A cell may contain multiple text objects
      texts = [];
      const textElements = cellElement.querySelectorAll('text');
      textElements.forEach(textElement => {
        const x = parseFloat(textElement.getAttribute('x'));
        const y = parseFloat(textElement.getAttribute('y'));
        const textContent = textElement.textContent;
        texts.push({ x, y, textContent });
      });
    }

    if (alignment) {
      textElement = textElement || cellElement.querySelector('g.text');
      textAnchor = textElement.getAttribute('text-anchor');

      const textAreaElement = cellElement.querySelector('g.textarea');
      for (let i = 0; i < textAreaElement.classList.length; i++) {
        const matchData = textAreaElement.classList[i].match(/vertical-(.+)/);
        if (matchData) {
          verticalAlignment = matchData[1];
          break;
        }
      }
    }

    return { fontSize, fontWeight, fontStyle, fontFamily, fill, texts, textAnchor, verticalAlignment, textDecoration, highlight, isOrderedList, isBulletsList, isUpperCase };
  },

  // Get the row element that holds this cell
  getCellRowElement (cellElement) {
    return cellElement.parentElement;
  },

  // Extract the merge operation id (an integer) from the cell
  getMergeId (cellElement) {
    let result = -1;

    [...cellElement.classList].forEach(className => {
    // If we know the merge id, don't iterate anymore
      if (result !== -1) return;

      const match = className.match(/m-(\d+)/);
      if (match) result = parseInt(match[1]);
    });

    return result;
  },

  // If the cell is part of a merge operation
  isMerged (cellElement) {
  // A normal cell doesn't have those classes
    const isNotMerged = ['table-cell-max', 'table-cell-min'].every(className => !cellElement.classList.contains(className));
    return !isNotMerged;
  },

  // If the cell is maximized as part of a merge operation
  isMergeMaster (cellElement) {
    return cellElement.classList.contains('table-cell-max');
  },

  // If the cell is minimized as part of a merge operation
  isMergeSlave (cellElement) {
    return cellElement.classList.contains('table-cell-min');
  },

  getMergeMaster (tableElement, mergeId) {
    return tableElement?.querySelector(`.table-cell-max.m-${mergeId}`);
  },

  // Get border description
  // Arguments are Booleans indicating if those info should be included
  getBorderData (borderElement, indices, position, visual) {
    let rowIndex = null;
    let columnIndex = null;

    if (indices) {
      const classList = [...borderElement.classList];

      let indexClassName = classList.find(className => className.startsWith('index-r'));
      rowIndex = parseInt(indexClassName.substr(8));

      indexClassName = classList.find(className => className.startsWith('index-c'));
      columnIndex = parseInt(indexClassName.substr(8));
    }

    let x1 = null;
    let x2 = null;
    let y1 = null;
    let y2 = null;

    if (position) {
      x1 = parseFloat(borderElement.getAttribute('x1'));
      x2 = parseFloat(borderElement.getAttribute('x2'));
      y1 = parseFloat(borderElement.getAttribute('y1'));
      y2 = parseFloat(borderElement.getAttribute('y2'));
    }

    let stroke = null;
    let strokeWidth = null;
    let strokeDash = null;

    if (visual) {
      stroke = borderElement.getAttribute('stroke');
      strokeWidth = parseFloat(borderElement.getAttribute('stroke-width'));
      strokeDash = parseInt(borderElement.getAttribute('stroke-dasharray')) || null;
    }

    return { rowIndex, columnIndex, x1, x2, y1, y2, stroke, strokeWidth, strokeDash };
  },

  // Get all borders of the table
  getAllBorderElements (tableElement, editMode) {
    return tableElement.querySelectorAll('line.table-border' + (editMode ? '-editor' : ''));
  },

  // Is the given border element vertical
  isVerticalBorder (borderElement) {
    return borderElement.classList.contains('table-border-v');
  },

  // Get some border elements at the given coordinates
  getBorderElementAt (tableElement, cellRowIndex, cellColumnIndex, which) {
    let row = 0;
    let column = 0;
    let direction = directionHorizontal;

    switch (which) {
      case borderFlagsLeft:
        row = cellRowIndex;
        column = cellColumnIndex;
        direction = directionVertical;
        break;
      case borderFlagsRight:
        row = cellRowIndex;
        column = cellColumnIndex + 1;
        direction = directionVertical;
        break;
      case borderFlagsTop:
        row = cellRowIndex;
        column = cellColumnIndex;
        break;
      case borderFlagsBottom:
        row = cellRowIndex + 1;
        column = cellColumnIndex;
        break;
    }

    const query = `g.table-borders line.table-border.table-border-${direction}.index-r-${row}.index-c-${column}`;
    return tableElement.querySelector(query);
  },

  // Get one of the (l)eft, (t)op, (r)ight or (b)ottom borders
  getBorderElementByCell (cellElement, which) {
    const cellData = this.getCellIndexData(cellElement);

    let direction = directionHorizontal;
    let rowIndex = 0;
    let columnIndex = 0;

    switch (which) {
      case borderFlagsLeft:
        rowIndex = cellData.rowIndex;
        columnIndex = cellData.columnIndex;
        direction = directionVertical;
        break;
      case borderFlagsRight:
        rowIndex = cellData.rowIndex;
        columnIndex = cellData.columnIndex + 1;
        direction = directionVertical;
        break;
      case borderFlagsTop:
        rowIndex = cellData.rowIndex;
        columnIndex = cellData.columnIndex;
        break;
      case borderFlagsBottom:
        rowIndex = cellData.rowIndex + 1;
        columnIndex = cellData.columnIndex;
        break;
    }

    // 1st parent is the cell's row
    // 2nd parent is the owner table
    const tableElement = cellElement.parentElement.parentElement;
    return tableElement.querySelector(`g.table-borders line.table-border-${direction}.index-r-${rowIndex}.index-c-${columnIndex}`);
  },

  // Get a collection of lines that form the top border of the row
  getRowTopBorderElements (rowElement) {
    const rowIndex = this.getRowIndexData(rowElement);
    return rowElement.parentElement.querySelectorAll(`g.table-borders line.table-border-h.index-r-${rowIndex}`);
  },

  // Get a collection of lines that form the bottom border of the row
  getRowBottomBorderElements (rowElement) {
    const rowIndex = this.getRowIndexData(rowElement);
    return rowElement.parentElement.querySelectorAll(`g.table-borders line.table-border-h.index-r-${rowIndex + 1}`);
  },

  // Get a collection of lines that form the vertical (exterior and interior) borders of the row
  getRowSideBorderElements (rowElement) {
    const rowIndex = this.getRowIndexData(rowElement);
    return rowElement.parentElement.querySelectorAll(`g.table-borders line.table-border-v.index-r-${rowIndex}`);
  },

  // Calculate the min height that table can be resized vertically to
  calculateVerticalLimit (tableElement) {
    const rowElements = this.getAllRowElements(tableElement);

    let minScale = 0;

    for (let i = 0; i < rowElements.length; i++) {
      const rowElement = rowElements[i];
      const cellElements = this.getAllCellElementsByRow(rowElement);

      for (let j = 0; j < cellElements.length; j++) {
        const cellElement = cellElements[j];
        const textElements = cellElement.querySelectorAll('text');
        const rectElement = cellElement.querySelector('rect');

        const firstTextElement = textElements[0];
        const lastTextElement = textElements[textElements.length - 1];
        const firstTextY = parseFloat(firstTextElement.getAttribute('y'));
        const lastTextY = parseFloat(lastTextElement.getAttribute('y'));
        const cellPadding = this.getCellPadding(cellElement);
        const textHeight = lastTextY - firstTextY + firstTextElement.getBBox().height + cellPadding.top + cellPadding.bottom;
        const rectHeight = parseFloat(rectElement.getAttribute('height'));

        const minHeight = Math.max(textHeight, cellSizeMin.height);
        minScale = Math.max(minScale, minHeight / rectHeight);
      }
    }

    return minScale;
  },

  getCellPadding (cellElement) {
    const defaultPadding = {
      top: 0,
      right: 0,
      bottom: 0,
      left: 0
    };

    const padding = cellElement?.dataset?.spacing;
    if (!padding) return defaultPadding;

    return {
      top: Number(padding.split(' ')[0]),
      right: Number(padding.split(' ')[1]),
      bottom: Number(padding.split(' ')[2]),
      left: Number(padding.split(' ')[3])
    };
  },

  /**
   *
   * @param {HTMLElement} tableElement
   * @param {string}mergeId
   * @returns {HTMLElement[]}
   */
  getMergedCells (tableElement, mergeId) {
    return [...tableElement.querySelectorAll(`.table-cell.m-${mergeId}`)];
  },

  /**
   *
   * @param {HTMLElement} cell
   * @returns {HTMLElement} The master cell of the merge operation or itself if it is not a slave
   */
  getMasterCell (cell) {
    if (!this.isMergeSlave(cell)) {
      return cell;
    }

    return this.getMergeMaster(cell.parentElement.parentElement, this.getMergeId(cell)) ?? cell;
  },

  /**
   *
   * @param {HTMLElement} cell
   * @returns {HTMLElement}
   */
  getRightBottomCell (cell) {
    if (!this.isMerged(cell)) return cell;
    const mergeId = this.getMergeId(cell);

    const mergedCells = this.getMergedCells(cell.parentElement.parentElement, mergeId);

    const { lastRow, lastColumn } = mergedCells.reduce((acc, cell) => {
      const { rowIndex, columnIndex } = this.getCellIndexData(cell);
      return { lastRow: Math.max(acc.lastRow, rowIndex), lastColumn: Math.max(acc.lastColumn, columnIndex) };
    }, { lastRow: 0, lastColumn: 0 });

    const rightBottomCell = mergedCells.find(cell => {
      const { columnIndex, rowIndex } = this.getCellIndexData(cell);
      return columnIndex === lastColumn && rowIndex === lastRow;
    });

    const leftBottomCell = mergedCells.find(cell => {
      const { columnIndex, rowIndex } = this.getCellIndexData(cell);
      return columnIndex === 0 && rowIndex === lastRow;
    });

    return rightBottomCell ?? leftBottomCell ?? cell;
  }
};
